if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupLumiyearCarousel();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => {
              this.thumbnailClickTarget = mediaToSwitch;
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupLumiyearCarousel() {
        this.lumiyearCarousel = {
          scroller: this.elements.viewer?.querySelector('.product__media-list'),
          slides: Array.from(this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]') || []),
          dots: Array.from(this.querySelectorAll('[data-lumiyear-product-media-dot]')),
          previousButtons: Array.from(this.querySelectorAll('[data-lumiyear-product-media-prev]')),
          nextButtons: Array.from(this.querySelectorAll('[data-lumiyear-product-media-next]')),
        };

        if (this.lumiyearCarousel.slides.length < 2) return;

        this.lumiyearCarousel.touchStartX = 0;
        this.lumiyearCarousel.scrollRaf = null;
        this.lumiyearCarousel.index = Math.max(
          0,
          this.lumiyearCarousel.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );

        this.lumiyearCarousel.previousButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.showLumiyearCarouselSlide(this.lumiyearCarousel.index - 1, 'previous');
          });
        });

        this.lumiyearCarousel.nextButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.showLumiyearCarouselSlide(this.lumiyearCarousel.index + 1, 'next');
          });
        });

        this.lumiyearCarousel.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const clickState = this.getLumiyearDotClickState(dot);
            this.lumiyearDotClickState = clickState;
            this.lumiyearDotClickTarget = dot;
            this.applyLumiyearDotEdgeShift(dot, clickState?.edge);
            const nextIndex = this.lumiyearCarousel.slides.findIndex((slide) => slide.dataset.mediaId === dot.dataset.lumiyearMediaId);
            this.showLumiyearCarouselSlide(
              nextIndex,
              nextIndex < this.lumiyearCarousel.index ? 'previous' : 'next'
            );
          });
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showLumiyearCarouselSlide(this.lumiyearCarousel.index - 1, 'previous');
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showLumiyearCarouselSlide(this.lumiyearCarousel.index + 1, 'next');
          }
        });

        this.elements.viewer?.addEventListener(
          'touchstart',
          (event) => {
            this.lumiyearCarousel.touchStartX = event.changedTouches[0]?.clientX || 0;
          },
          { passive: true }
        );

        this.elements.viewer?.addEventListener(
          'touchend',
          (event) => {
            if (this.isLumiyearMobileCarousel()) return;

            const touchEndX = event.changedTouches[0]?.clientX || 0;
            const delta = touchEndX - this.lumiyearCarousel.touchStartX;

            if (Math.abs(delta) < 42) return;

            if (delta > 0) this.showLumiyearCarouselSlide(this.lumiyearCarousel.index - 1, 'previous');
            else this.showLumiyearCarouselSlide(this.lumiyearCarousel.index + 1, 'next');
          },
          { passive: true }
        );

        this.lumiyearCarousel.scroller?.addEventListener(
          'scroll',
          () => {
            if (!this.isLumiyearMobileCarousel()) return;
            this.queueLumiyearMobileScrollSync();
          },
          { passive: true }
        );

        this.syncLumiyearCarousel(this.lumiyearCarousel.slides[this.lumiyearCarousel.index]);
      }

      isLumiyearMobileCarousel() {
        return !this.mql.matches;
      }

      queueLumiyearMobileScrollSync() {
        if (!this.lumiyearCarousel || this.lumiyearCarousel.scrollRaf) return;

        this.lumiyearCarousel.scrollRaf = window.requestAnimationFrame(() => {
          this.lumiyearCarousel.scrollRaf = null;
          this.syncLumiyearMobileScrollState();
        });
      }

      syncLumiyearMobileScrollState() {
        const scroller = this.lumiyearCarousel?.scroller;
        const slides = this.lumiyearCarousel?.slides || [];
        if (!scroller || !slides.length) return;

        const scrollerRect = scroller.getBoundingClientRect();
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
        let activeSlide = slides[0];
        let activeDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - scrollerCenter);

          if (distance < activeDistance) {
            activeDistance = distance;
            activeSlide = slide;
          }
        });

        if (!activeSlide || this.lumiyearCarousel.index === slides.indexOf(activeSlide)) return;

        this.syncLumiyearCarousel(activeSlide, { keepDotPosition: true });
      }

      showLumiyearCarouselSlide(nextIndex, direction = 'next') {
        if (!this.lumiyearCarousel?.slides?.length) return;

        const boundedIndex = Math.max(0, Math.min(nextIndex, this.lumiyearCarousel.slides.length - 1));
        const activeSlide = this.lumiyearCarousel.slides[boundedIndex];

        if (!activeSlide) return;

        this.dataset.direction = direction;
        this.setActiveMedia(activeSlide.dataset.mediaId, false);
      }

      scrollLumiyearMobileMedia(activeMedia) {
        const scroller = this.lumiyearCarousel?.scroller;
        if (!scroller || !activeMedia) return;

        const centeredLeft = activeMedia.offsetLeft - (scroller.clientWidth - activeMedia.clientWidth) / 2;
        scroller.scrollTo({ left: centeredLeft, behavior: 'smooth' });
      }

      syncLumiyearCarousel(activeMedia, options = {}) {
        if (!this.lumiyearCarousel?.slides?.length || !activeMedia) return;

        const activeIndex = Math.max(0, this.lumiyearCarousel.slides.indexOf(activeMedia));
        this.lumiyearCarousel.index = activeIndex;
        const isMobileCarousel = this.isLumiyearMobileCarousel();

        this.lumiyearCarousel.slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isMobileCarousel || isActive ? 'false' : 'true');
        });

        this.lumiyearCarousel.dots.forEach((dot) => {
          const isActive = dot.dataset.lumiyearMediaId === activeMedia.dataset.mediaId;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const activeDot = this.lumiyearCarousel.dots[activeIndex];
        if (activeDot) {
          this.lumiyearDotClickTarget = null;
          this.lumiyearDotClickState = null;
          if (!options.keepDotPosition) this.ensureLumiyearDotVisible(activeDot);
        }

        this.lumiyearCarousel.previousButtons.forEach((button) => {
          button.disabled = activeIndex === 0;
        });
        this.lumiyearCarousel.nextButtons.forEach((button) => {
          button.disabled = activeIndex === this.lumiyearCarousel.slides.length - 1;
        });
      }

      ensureLumiyearDotVisible(dot) {
        const dotScroller = dot?.parentElement;
        if (!dotScroller) return;

        this.updateLumiyearDotScrollerPadding(dotScroller);

        const dotRect = dot.getBoundingClientRect();
        const scrollerRect = dotScroller.getBoundingClientRect();
        const tolerance = 2;

        if (dotRect.left >= scrollerRect.left + tolerance && dotRect.right <= scrollerRect.right - tolerance) return;

        const maxScrollLeft = Math.max(0, dotScroller.scrollWidth - dotScroller.clientWidth);
        let targetLeft = dotScroller.scrollLeft;

        if (dotRect.left < scrollerRect.left + tolerance) {
          targetLeft += dotRect.left - scrollerRect.left - 8;
        } else if (dotRect.right > scrollerRect.right - tolerance) {
          targetLeft += dotRect.right - scrollerRect.right + 8;
        }

        targetLeft = Math.max(0, Math.min(maxScrollLeft, targetLeft));
        dotScroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      getLumiyearDotClickState(dot) {
        const dotScroller = dot?.parentElement;
        const dots = this.lumiyearCarousel?.dots || [];
        if (!dotScroller || dots.length < 2) return null;

        this.updateLumiyearDotScrollerPadding(dotScroller);

        const activeIndex = dots.indexOf(dot);
        if (activeIndex <= 0 || activeIndex >= dots.length - 1) {
          return {
            edge: null,
            mediaId: dot.dataset.lumiyearMediaId,
          };
        }

        const scrollerRect = dotScroller.getBoundingClientRect();
        const tolerance = 2;
        const visibleDots = dots.filter((item) => {
          const rect = item.getBoundingClientRect();
          return rect.left >= scrollerRect.left + tolerance && rect.right <= scrollerRect.right - tolerance;
        });

        if (visibleDots.length < 2) {
          return {
            edge: null,
            mediaId: dot.dataset.lumiyearMediaId,
          };
        }

        const firstVisibleIndex = dots.indexOf(visibleDots[0]);
        const lastVisibleIndex = dots.indexOf(visibleDots[visibleDots.length - 1]);

        if (activeIndex === lastVisibleIndex) {
          return {
            edge: 'forward',
            mediaId: dot.dataset.lumiyearMediaId,
          };
        }

        if (activeIndex === firstVisibleIndex) {
          return {
            edge: 'backward',
            mediaId: dot.dataset.lumiyearMediaId,
          };
        }

        return {
          edge: null,
          mediaId: dot.dataset.lumiyearMediaId,
        };
      }

      applyLumiyearDotEdgeShift(dot, edge) {
        const dotScroller = dot?.parentElement;
        if (!dotScroller || !edge) return false;

        this.updateLumiyearDotScrollerPadding(dotScroller);

        const dotRect = dot.getBoundingClientRect();
        const scrollerRect = dotScroller.getBoundingClientRect();
        const maxScrollLeft = Math.max(0, dotScroller.scrollWidth - dotScroller.clientWidth);

        let targetLeft = null;

        if (edge === 'forward') {
          targetLeft = dotScroller.scrollLeft + (dotRect.left - scrollerRect.left) - 8;
        } else if (edge === 'backward') {
          targetLeft = dotScroller.scrollLeft + (dotRect.right - scrollerRect.right) + 8;
        }

        if (targetLeft === null) return false;

        targetLeft = Math.max(0, Math.min(maxScrollLeft, targetLeft));
        if (Math.abs(targetLeft - dotScroller.scrollLeft) < 1) return false;

        dotScroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
        return true;
      }

      updateLumiyearDotScrollerPadding(dotScroller) {
        const firstDot = dotScroller?.querySelector('.lumiyear-product-media-carousel__dot');
        if (!dotScroller || !firstDot) return;

        const spacerWidth = Math.max(0, dotScroller.clientWidth - firstDot.offsetWidth - 8);
        dotScroller.style.setProperty('--lumiyear-dot-end-spacer', `${spacerWidth}px`);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.isLumiyearMobileCarousel()) {
            this.scrollLumiyearMobileMedia(activeMedia);
          } else if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncLumiyearCarousel(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.thumbnailClickTarget === thumbnail) {
          this.thumbnailClickTarget = null;
          if (this.shiftThumbnailViewportFromEdge(thumbnail)) return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      shiftThumbnailViewportFromEdge(thumbnail) {
        const thumbnailSlider = this.elements.thumbnails;
        const thumbnailItems = thumbnailSlider.sliderItemsToShow || Array.from(thumbnailSlider.querySelectorAll('.thumbnail-list__item'));
        if (!thumbnailSlider.slider || thumbnailItems.length < 2) return false;

        const activeIndex = thumbnailItems.indexOf(thumbnail);
        if (activeIndex <= 0 || activeIndex >= thumbnailItems.length - 1) return false;

        const visibleItems = thumbnailItems.filter((item) => thumbnailSlider.isSlideVisible(item, 10));
        if (visibleItems.length < 2) return false;

        const firstVisibleIndex = thumbnailItems.indexOf(visibleItems[0]);
        const lastVisibleIndex = thumbnailItems.indexOf(visibleItems[visibleItems.length - 1]);
        const visibleCount = visibleItems.length;

        let targetIndex = null;

        if (activeIndex === lastVisibleIndex) {
          targetIndex = activeIndex;
        } else if (activeIndex === firstVisibleIndex) {
          targetIndex = Math.max(0, activeIndex - visibleCount + 1);
        }

        if (targetIndex === null || targetIndex === firstVisibleIndex) return false;

        const targetItem = thumbnailItems[targetIndex];
        if (!targetItem) return false;

        thumbnailSlider.slider.scrollTo({ left: targetItem.offsetLeft });
        return true;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
